import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, Search, Filter, AlertTriangle, AlertCircle, FileWarning, 
  Clock, CheckCircle, Edit, Trash2, Eye, Calendar, MapPin, User,
  FileText, Upload, X, MessageSquare
} from 'lucide-react';

const INCIDENT_TYPES = [
  { value: 'incidente', label: 'Incidente', icon: AlertTriangle, color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'near_miss', label: 'Near Miss', icon: AlertCircle, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'non_conformita', label: 'Non Conformita', icon: FileWarning, color: 'bg-orange-100 text-orange-700 border-orange-200' }
];

const SEVERITY_LEVELS = [
  { value: 'bassa', label: 'Bassa', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'media', label: 'Media', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'alta', label: 'Alta', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'critica', label: 'Critica', color: 'bg-red-100 text-red-700 border-red-200' }
];

const STATUS_OPTIONS = [
  { value: 'aperta', label: 'Aperta', icon: Clock, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'in_risoluzione', label: 'In Risoluzione', icon: Edit, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'chiusa', label: 'Chiusa', icon: CheckCircle, color: 'bg-green-100 text-green-700 border-green-200' }
];

export default function IncidentsPage() {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  
  // Filters
  const [filterType, setFilterType] = useState('__all__');
  const [filterStatus, setFilterStatus] = useState('__all__');
  const [filterSeverity, setFilterSeverity] = useState('__all__');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [form, setForm] = useState({
    incident_type: 'non_conformita',
    title: '',
    description: '',
    severity: 'media',
    status: 'aperta',
    site_id: '',
    location_details: '',
    responsible_id: '',
    reported_by_id: '',
    incident_date: new Date().toISOString().split('T')[0],
    reported_date: new Date().toISOString().split('T')[0],
    resolution_deadline: '',
    root_cause: '',
    corrective_actions: '',
    preventive_actions: '',
    resolution_notes: ''
  });

  const [editForm, setEditForm] = useState({...form});
  const [commentText, setCommentText] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = null;

  useEffect(() => {
    load();
  }, [filterType, filterStatus, filterSeverity]);

  const load = async () => {
    try {
      const params = {};
      if (filterType !== '__all__') params.incident_type = filterType;
      if (filterStatus !== '__all__') params.status = filterStatus;
      if (filterSeverity !== '__all__') params.severity = filterSeverity;
      
      const [incRes, sitesRes, empRes] = await Promise.all([
        api.get('/incidents', { params }),
        api.get('/sites'),
        api.get('/employees/list/simple')
      ]);
      setIncidents(incRes.data);
      setSites(sitesRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.post('/incidents', form);
      setShowCreate(false);
      resetForm();
      load();
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdate = async () => {
    try {
      await api.put(`/incidents/${selectedIncident.id}`, editForm);
      setShowEdit(false);
      load();
      // Reload detail if open
      if (showDetail) {
        const res = await api.get(`/incidents/${selectedIncident.id}`);
        setSelectedIncident(res.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa segnalazione?')) return;
    try {
      await api.delete(`/incidents/${id}`);
      load();
      if (showDetail && selectedIncident?.id === id) {
        setShowDetail(false);
        setSelectedIncident(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post(`/incidents/${selectedIncident.id}/comments`, { text: commentText });
      setCommentText('');
      const res = await api.get(`/incidents/${selectedIncident.id}`);
      setSelectedIncident(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUploadAttachment = async (file) => {
    if (!file || !selectedIncident) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/incidents/${selectedIncident.id}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const res = await api.get(`/incidents/${selectedIncident.id}`);
      setSelectedIncident(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore upload allegato');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Eliminare questo allegato?')) return;
    try {
      await api.delete(`/incidents/${selectedIncident.id}/attachments/${attachmentId}`);
      const res = await api.get(`/incidents/${selectedIncident.id}`);
      setSelectedIncident(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore eliminazione allegato');
    }
  };

  const openDetail = async (incident) => {
    try {
      const res = await api.get(`/incidents/${incident.id}`);
      setSelectedIncident(res.data);
      setShowDetail(true);
    } catch (err) {
      console.error(err);
    }
  };

  const openEdit = (incident) => {
    setEditForm({
      incident_type: incident.incident_type || 'non_conformita',
      title: incident.title || '',
      description: incident.description || '',
      severity: incident.severity || 'media',
      status: incident.status || 'aperta',
      site_id: incident.site_id || '',
      location_details: incident.location_details || '',
      responsible_id: incident.responsible_id || '',
      reported_by_id: incident.reported_by_id || '',
      incident_date: incident.incident_date || '',
      reported_date: incident.reported_date || '',
      resolution_deadline: incident.resolution_deadline || '',
      root_cause: incident.root_cause || '',
      corrective_actions: incident.corrective_actions || '',
      preventive_actions: incident.preventive_actions || '',
      resolution_notes: incident.resolution_notes || ''
    });
    setSelectedIncident(incident);
    setShowEdit(true);
  };

  const resetForm = () => {
    setForm({
      incident_type: 'non_conformita',
      title: '',
      description: '',
      severity: 'media',
      status: 'aperta',
      site_id: '',
      location_details: '',
      responsible_id: '',
      reported_by_id: '',
      incident_date: new Date().toISOString().split('T')[0],
      reported_date: new Date().toISOString().split('T')[0],
      resolution_deadline: '',
      root_cause: '',
      corrective_actions: '',
      preventive_actions: '',
      resolution_notes: ''
    });
  };

  const getTypeInfo = (type) => INCIDENT_TYPES.find(t => t.value === type) || INCIDENT_TYPES[2];
  const getSeverityInfo = (severity) => SEVERITY_LEVELS.find(s => s.value === severity) || SEVERITY_LEVELS[1];
  const getStatusInfo = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

  const filteredIncidents = incidents.filter(inc => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return inc.title?.toLowerCase().includes(q) || 
             inc.description?.toLowerCase().includes(q) ||
             inc.site_name?.toLowerCase().includes(q);
    }
    return true;
  });

  // Stats
  const openCount = incidents.filter(i => i.status !== 'chiusa').length;
  const criticalCount = incidents.filter(i => i.severity === 'critica' && i.status !== 'chiusa').length;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="incidents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestione Segnalazioni</h1>
          <p className="text-sm text-slate-500">Incidenti, Near Miss e Non Conformita</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-orange-600 hover:bg-orange-700" data-testid="new-incident-btn">
          <Plus size={16} className="mr-2" />
          Nuova Segnalazione
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileWarning size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{incidents.length}</p>
              <p className="text-xs text-slate-500">Totale</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{openCount}</p>
              <p className="text-xs text-slate-500">Aperte</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{criticalCount}</p>
              <p className="text-xs text-slate-500">Critiche</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{incidents.filter(i => i.status === 'chiusa').length}</p>
              <p className="text-xs text-slate-500">Risolte</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Cerca segnalazioni..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i tipi</SelectItem>
                {INCIDENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Stato" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti gli stati</SelectItem>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterSeverity} onValueChange={setFilterSeverity}>
              <SelectTrigger className="w-[160px]"><SelectValue placeholder="Gravita" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                {SEVERITY_LEVELS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Segnalazione</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Gravita</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Cantiere</TableHead>
                <TableHead>Allegati</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead className="w-24">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredIncidents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-400">
                    Nessuna segnalazione trovata
                  </TableCell>
                </TableRow>
              ) : filteredIncidents.map(inc => {
                const typeInfo = getTypeInfo(inc.incident_type);
                const severityInfo = getSeverityInfo(inc.severity);
                const statusInfo = getStatusInfo(inc.status);
                const TypeIcon = typeInfo.icon;
                
                return (
                  <TableRow key={inc.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(inc)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TypeIcon size={16} className={typeInfo.color.split(' ')[1]} />
                        <div>
                          <p className="font-medium text-sm">{inc.title}</p>
                          <p className="text-xs text-slate-500 line-clamp-1">{inc.description}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={typeInfo.color}>{typeInfo.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={severityInfo.color}>{severityInfo.label}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusInfo.color}>{statusInfo.label}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{inc.site_name || '-'}</TableCell>
                    <TableCell className="text-sm">
                      {(inc.attachments?.length || 0) > 0 ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid={`att-count-${inc.id}`}>
                          <FileText size={12} className="mr-1" />{inc.attachments.length}
                        </Badge>
                      ) : <span className="text-slate-400">-</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {inc.resolution_deadline ? (
                        <span className={inc.status !== 'chiusa' && new Date(inc.resolution_deadline) < new Date() ? 'text-red-600 font-medium' : ''}>
                          {new Date(inc.resolution_deadline).toLocaleDateString('it-IT')}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={() => openDetail(inc)}>
                          <Eye size={14} className="text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={() => openEdit(inc)}>
                          <Edit size={14} className="text-blue-600" />
                        </Button>
                        <Button variant="ghost" size="sm" className="p-1 h-auto" onClick={() => handleDelete(inc.id)}>
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Segnalazione</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="generale" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="generale">Generale</TabsTrigger>
              <TabsTrigger value="dettagli">Dettagli</TabsTrigger>
              <TabsTrigger value="risoluzione">Risoluzione</TabsTrigger>
            </TabsList>
            
            <TabsContent value="generale" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Tipo Segnalazione *</Label>
                  <Select value={form.incident_type} onValueChange={(v) => setForm({...form, incident_type: v})}>
                    <SelectTrigger data-testid="incident-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INCIDENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Gravita *</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({...form, severity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEVERITY_LEVELS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Titolo *</Label>
                <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Breve descrizione dell'evento" data-testid="incident-title" />
              </div>
              <div className="space-y-1.5">
                <Label>Descrizione Dettagliata</Label>
                <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} placeholder="Descrivi cosa e successo..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Cantiere</Label>
                  <Select value={form.site_id || '__none__'} onValueChange={(v) => setForm({...form, site_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-</SelectItem>
                      {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Luogo Specifico</Label>
                  <Input value={form.location_details} onChange={(e) => setForm({...form, location_details: e.target.value})} placeholder="Es. Area verniciatura" />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="dettagli" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Data Evento</Label>
                  <Input type="date" value={form.incident_date} onChange={(e) => setForm({...form, incident_date: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <Label>Data Segnalazione</Label>
                  <Input type="date" value={form.reported_date} onChange={(e) => setForm({...form, reported_date: e.target.value})} />
                </div>
                <div className="space-y-1.5">
                  <Label>Responsabile Risoluzione</Label>
                  <Select value={form.responsible_id || '__none__'} onValueChange={(v) => setForm({...form, responsible_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-</SelectItem>
                      {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Segnalato da</Label>
                  <Select value={form.reported_by_id || '__none__'} onValueChange={(v) => setForm({...form, reported_by_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-</SelectItem>
                      {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label>Scadenza Risoluzione</Label>
                  <Input type="date" value={form.resolution_deadline} onChange={(e) => setForm({...form, resolution_deadline: e.target.value})} />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="risoluzione" className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label>Causa Radice</Label>
                <Textarea value={form.root_cause} onChange={(e) => setForm({...form, root_cause: e.target.value})} rows={2} placeholder="Analisi della causa principale..." />
              </div>
              <div className="space-y-1.5">
                <Label>Azioni Correttive</Label>
                <Textarea value={form.corrective_actions} onChange={(e) => setForm({...form, corrective_actions: e.target.value})} rows={2} placeholder="Azioni per correggere il problema..." />
              </div>
              <div className="space-y-1.5">
                <Label>Azioni Preventive</Label>
                <Textarea value={form.preventive_actions} onChange={(e) => setForm({...form, preventive_actions: e.target.value})} rows={2} placeholder="Azioni per prevenire recidive..." />
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
            <Button onClick={handleCreate} disabled={!form.title} className="bg-orange-600 hover:bg-orange-700" data-testid="save-incident-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Segnalazione</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="generale" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="generale">Generale</TabsTrigger>
              <TabsTrigger value="dettagli">Dettagli</TabsTrigger>
              <TabsTrigger value="risoluzione">Risoluzione</TabsTrigger>
            </TabsList>
            
            <TabsContent value="generale" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Tipo Segnalazione</Label>
                  <Select value={editForm.incident_type} onValueChange={(v) => setEditForm({...editForm, incident_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INCIDENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Gravita</Label>
                  <Select value={editForm.severity} onValueChange={(v) => setEditForm({...editForm, severity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SEVERITY_LEVELS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Stato</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Titolo</Label>
                <Input value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <Label>Descrizione</Label>
                <Textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} rows={3} />
              </div>
            </TabsContent>
            
            <TabsContent value="dettagli" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Cantiere</Label>
                  <Select value={editForm.site_id || '__none__'} onValueChange={(v) => setEditForm({...editForm, site_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-</SelectItem>
                      {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Responsabile</Label>
                  <Select value={editForm.responsible_id || '__none__'} onValueChange={(v) => setEditForm({...editForm, responsible_id: v === '__none__' ? '' : v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-</SelectItem>
                      {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.last_name} {e.first_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Scadenza Risoluzione</Label>
                  <Input type="date" value={editForm.resolution_deadline} onChange={(e) => setEditForm({...editForm, resolution_deadline: e.target.value})} />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="risoluzione" className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label>Causa Radice</Label>
                <Textarea value={editForm.root_cause} onChange={(e) => setEditForm({...editForm, root_cause: e.target.value})} rows={2} />
              </div>
              <div className="space-y-1.5">
                <Label>Azioni Correttive</Label>
                <Textarea value={editForm.corrective_actions} onChange={(e) => setEditForm({...editForm, corrective_actions: e.target.value})} rows={2} />
              </div>
              <div className="space-y-1.5">
                <Label>Azioni Preventive</Label>
                <Textarea value={editForm.preventive_actions} onChange={(e) => setEditForm({...editForm, preventive_actions: e.target.value})} rows={2} />
              </div>
              <div className="space-y-1.5">
                <Label>Note Risoluzione</Label>
                <Textarea value={editForm.resolution_notes} onChange={(e) => setEditForm({...editForm, resolution_notes: e.target.value})} rows={2} />
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Annulla</Button>
            <Button onClick={handleUpdate} disabled={!editForm.title}>Salva Modifiche</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedIncident && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="flex items-center gap-2">
                      {(() => {
                        const TypeIcon = getTypeInfo(selectedIncident.incident_type).icon;
                        return <TypeIcon size={20} className={getTypeInfo(selectedIncident.incident_type).color.split(' ')[1]} />;
                      })()}
                      {selectedIncident.title}
                    </DialogTitle>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline" className={getTypeInfo(selectedIncident.incident_type).color}>
                        {getTypeInfo(selectedIncident.incident_type).label}
                      </Badge>
                      <Badge variant="outline" className={getSeverityInfo(selectedIncident.severity).color}>
                        {getSeverityInfo(selectedIncident.severity).label}
                      </Badge>
                      <Badge variant="outline" className={getStatusInfo(selectedIncident.status).color}>
                        {getStatusInfo(selectedIncident.status).label}
                      </Badge>
                    </div>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {selectedIncident.description && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-1">Descrizione</p>
                    <p className="text-sm text-slate-600">{selectedIncident.description}</p>
                  </div>
                )}
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin size={14} className="text-slate-400" />
                    <span className="text-slate-600">{selectedIncident.site_name || 'N/A'} {selectedIncident.location_details && `- ${selectedIncident.location_details}`}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User size={14} className="text-slate-400" />
                    <span className="text-slate-600">Responsabile: {selectedIncident.responsible_name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar size={14} className="text-slate-400" />
                    <span className="text-slate-600">Data evento: {selectedIncident.incident_date ? new Date(selectedIncident.incident_date).toLocaleDateString('it-IT') : 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock size={14} className="text-slate-400" />
                    <span className={selectedIncident.status !== 'chiusa' && selectedIncident.resolution_deadline && new Date(selectedIncident.resolution_deadline) < new Date() ? 'text-red-600 font-medium' : 'text-slate-600'}>
                      Scadenza: {selectedIncident.resolution_deadline ? new Date(selectedIncident.resolution_deadline).toLocaleDateString('it-IT') : 'N/A'}
                    </span>
                  </div>
                </div>
                
                {(selectedIncident.root_cause || selectedIncident.corrective_actions || selectedIncident.preventive_actions) && (
                  <div className="border-t pt-4 space-y-3">
                    {selectedIncident.root_cause && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-1">Causa Radice</p>
                        <p className="text-sm text-slate-600">{selectedIncident.root_cause}</p>
                      </div>
                    )}
                    {selectedIncident.corrective_actions && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-1">Azioni Correttive</p>
                        <p className="text-sm text-slate-600">{selectedIncident.corrective_actions}</p>
                      </div>
                    )}
                    {selectedIncident.preventive_actions && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-1">Azioni Preventive</p>
                        <p className="text-sm text-slate-600">{selectedIncident.preventive_actions}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Attachments Section */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <FileText size={14} /> Allegati ({selectedIncident.attachments?.length || 0})
                  </p>
                  
                  {/* Upload button */}
                  <div className="mb-3">
                    <label className="inline-block">
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                        onChange={(e) => { if (e.target.files?.[0]) handleUploadAttachment(e.target.files[0]); e.target.value = ''; }}
                        data-testid="attachment-file-input"
                      />
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm cursor-pointer transition-colors">
                        <Upload size={14} />
                        {uploading ? 'Caricamento...' : 'Carica allegato'}
                      </span>
                    </label>
                    <span className="text-xs text-slate-400 ml-2">PDF, immagini, DOC</span>
                  </div>

                  {/* Attachments list */}
                  {selectedIncident.attachments?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedIncident.attachments.map(att => {
                        const isImage = /\.(jpg|jpeg|png|gif)$/i.test(att.original_name || att.filename);
                        const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${att.file_url}`;
                        return (
                          <div key={att.id} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg border border-slate-100" data-testid={`attachment-${att.id}`}>
                            {isImage ? (
                              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
                                <img src={fileUrl} alt={att.original_name} className="w-12 h-12 rounded object-cover border border-slate-200" />
                              </a>
                            ) : (
                              <div className="w-12 h-12 rounded bg-slate-200 flex items-center justify-center shrink-0">
                                <FileText size={20} className="text-slate-500" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-blue-600 hover:underline truncate block">
                                {att.original_name || att.filename}
                              </a>
                              <p className="text-xs text-slate-400">
                                {att.uploaded_at ? new Date(att.uploaded_at).toLocaleString('it-IT') : ''}
                              </p>
                            </div>
                            <Button variant="ghost" size="sm" className="p-1 h-auto text-red-500 hover:text-red-700 shrink-0" onClick={() => handleDeleteAttachment(att.id)} data-testid={`del-att-${att.id}`}>
                              <X size={14} />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400">Nessun allegato</p>
                  )}
                </div>

                {/* Comments Section */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <MessageSquare size={14} /> Note e Commenti
                  </p>
                  <div className="space-y-2 max-h-[150px] overflow-y-auto mb-3">
                    {selectedIncident.comments?.length > 0 ? selectedIncident.comments.map(c => (
                      <div key={c.id} className="bg-slate-50 rounded-lg p-2 text-sm">
                        <p className="text-slate-600">{c.text}</p>
                        <p className="text-xs text-slate-400 mt-1">{c.created_by_email} - {new Date(c.created_at).toLocaleString('it-IT')}</p>
                      </div>
                    )) : (
                      <p className="text-sm text-slate-400">Nessun commento</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Aggiungi un commento..." 
                      value={commentText} 
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                    />
                    <Button variant="outline" size="sm" onClick={handleAddComment} disabled={!commentText.trim()}>
                      Invia
                    </Button>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetail(false)}>Chiudi</Button>
                <Button onClick={() => { setShowDetail(false); openEdit(selectedIncident); }}>
                  <Edit size={14} className="mr-2" /> Modifica
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
